// src/ApiPanel/constants/apiPanelAuth.js
// ============================================================================
// SINGLE SOURCE OF TRUTH FOR API PANEL AUTH.
//
// This panel keeps its own storage key and its own session state, so logging
// in/out here has no effect on the main site and vice versa. What it does NOT
// do any more is invent its own backend: every endpoint below is a real
// Mokshiq API route, and the token is the same JWT that account/auth.py
// issues and validates.
//
// THREE THINGS ABOUT THIS API THAT WILL BITE YOU IF YOU FORGET THEM
// -----------------------------------------------------------------
// 1. Login is TWO steps. `login_request` validates phone + password and sends
//    an OTP; `login_with_otp` exchanges phone + otp + password for the JWT.
//    There is no single-shot login, by design — do not add one.
//
// 2. The JWT is BOUND TO A CLIENT ID. `account/utils/jwt.get_client_id` reads
//    the `Origin` header FIRST and only falls back to `X-Client-ID`. Browsers
//    set Origin automatically, so the panel does not send X-Client-ID and does
//    not need to: the value baked into the token at login is the Origin, and
//    the Origin on every later request is the same. Sending the header as well
//    is what caused the "x-client-id is not allowed by
//    Access-Control-Allow-Headers" CORS failure — see utils/apiPanelFetch.js.
//
// 3. The login/logout views read `request.POST`, which is empty for a JSON
//    body. Those calls must be form-encoded. `apiPanelFetch` handles this via
//    the `form: true` option; do not "tidy" them into JSON.stringify.
// ============================================================================

export const API_PANEL_TOKEN_KEY = "apiPanelAuthToken";

export const API_PANEL_BASE_URL =
  process.env.REACT_APP_API_PANEL_BASE_URL ||
  process.env.REACT_APP_API_BASE ||
  "https://pro.mokshiq.in";

// OPT-IN ONLY, and empty by default. Leave it unset and the panel relies on
// the Origin header, which the browser sends for you.
//
// Set it only if you genuinely need X-Client-ID — the panel served from the
// SAME origin as the API (browsers omit Origin on same-origin requests), or a
// non-browser client. If you do, you MUST also allow the header server-side or
// every request is blocked by CORS preflight:
//
//     # settings.py
//     from corsheaders.defaults import default_headers
//     CORS_ALLOW_HEADERS = list(default_headers) + ["x-client-id"]
//
// The value must be a host that exists in your Company table as `website` or
// `smtp_port`, or login fails with "Invalid domain".
export const API_PANEL_CLIENT_ID = process.env.REACT_APP_API_PANEL_CLIENT_ID || "";

// The backend's detect_platform() returns 'web' when no X-Platform header is
// present, which is what this panel wants — so the header is not sent. (It is
// not on the CORS allow-list either.)
export const API_PANEL_PLATFORM = "web";

export const API_PANEL_ENDPOINTS = {
  // ---- auth (existing Mokshiq routes) ----
  LOGIN_REQUEST: "/login_request",     // POST form: phone, password  -> sends OTP
  LOGIN_WITH_OTP: "/login_with_otp",   // POST form: phone, otp, password -> { token }
  LOGOUT_THIS_DEVICE: "/logout_this_device", // POST -> revokes only this JWT
  LOGOUT_ALL: "/logout_view",          // POST -> revokes every session
  ME: "/auth/me",                      // GET  -> { data: { id, name, role, ... } }
  LOGIN_HISTORY: "/login/history/",    // GET  -> { data: [...] }

  // ---- CA REPORT ----
  CA_MONTHLY_LEDGER: "/ca/monthly_ledger",
  CA_SELL_INVOICE: "/ca/sell_invoice",
  CA_SELL_CMS_INVOICE: "/ca/sell_cms_invoice",
  CA_SELL_RECHARGE_INVOICE: "/ca/sell_recharge_invoice",
  CA_PURCHASE_INVOICE: "/ca/purchase_invoice",
  CA_PURCHASE_RECHARGE_INVOICE: "/ca/purchase_recharge_invoice",
  CA_PURCHASE_UTILITY_INVOICE: "/ca/purchase_utility_invoice",
  CA_INVOICE_DETAIL: "/ca/invoice_detail",
  CA_TDS: "/ca/tds_report",
  CA_CASH_DECLARATION: "/ca/cash_declaration",
  CA_CASH_DECLARATION_ACTION: "/ca/cash_declaration_action",

  // ---- CA REPORT: uploads ----
  CA_UPLOAD_GST: "/ca/upload_gst",
  CA_UPLOAD_GST_RECHARGE: "/ca/upload_gst_recharge",
  CA_UPLOAD_CASH_DECLARATION: "/ca/upload_cash_declaration",
  CA_UPLOAD_TEMPLATE: "/ca/upload_template",
  CA_UPLOAD_BATCH_LIST: "/ca/upload_batch_list",
  CA_UPLOAD_BATCH_DETAIL: "/ca/upload_batch_detail",
  CA_UPLOAD_BATCH_COMMIT: "/ca/upload_batch_commit",
  CA_UPLOAD_BATCH_REJECT: "/ca/upload_batch_reject",

  // ---- GST REPORT ----
  GST_INFORMATION: "/gst/information",
  GST_PAY_REPORT: "/gst/pay_report",
  GST_PAY_ACTION: "/gst/pay_action",
  GST_REVERSAL_REPORT: "/gst/reversal_report",
  GST_REVERSAL_ACTION: "/gst/reversal_action",

  // ---- TRANSACTION REPORT ----
  // All of these share one contract: GET with ?user=<account id>&filter_date=
  // &end_date=, answering {"data": [...]}. No pagination, no totals, and the
  // row keys differ by the caller's role — which is why the table derives its
  // columns from the rows instead of declaring them.
  TXN_RECHARGE: "/get_recharge_report",
  TXN_BBPS: "/bbps_report_data",
  TXN_OFFLINE_BBPS: "/get_offline_bbps_report",
  TXN_UPI_QR: "/upi_qr_reports_data",
  TXN_AEPS: "/get_aeps_report",
  TXN_MATM: "/get_matm_report",
  TXN_DMT: "/get_dmt_report",
  TXN_CREDIT_DEBIT: "/credit_debit_action",
  // NOT CONFIRMED: the pancard report view exists in ReportsViews.py, but I
  // could not find its path in urls.py. Check the real name before relying on
  // this screen — if it differs, change it here and nothing else moves.
  TXN_PANCARD: "/get_pancard_report",

  // ---- Token & API Request (real routes) ----
  // One key per partner, three actions: GET_API_KEY / GENERATE_API_KEY /
  // REVOKE_API_KEY. Form-encoded — the view reads request.POST.
  API_PARTNER_ACTION: "/api_partner_action",
  // SUBMIT / GET / GETBYID / APPROVE / REJECT, multipart for the document.
  API_INTEGRATION: "/api/api-integration/",

  // ---- Complaint / ticketing ----
  // One view, many actions: GET, CREATE, UPDATE, UPDATE_STATUS, DELETE,
  // GET_COMMENTS, ADD_COMMENT, GET_AGENTS. Form-encoded.
  // NOTE: this one answers {"status": "success" | "error"} as a STRING, not a
  // boolean like the rest of the API — apiPanelFetch handles both.
  TICKET_ACTION: "/ticket_manager_action",

  // ---- Header wallet strip ----
  // Shape varies by role, so the header reads it defensively and hides the
  // strip entirely rather than showing a figure it is not sure about.
  WALLET_SUMMARY: "/api_partner_dashboard_api",
};

export const API_PANEL_ROOT = "/api-panel";
export const API_PANEL_LOGIN_PATH = `${API_PANEL_ROOT}/login`;
export const API_PANEL_HOME_PATH = `${API_PANEL_ROOT}/dashboard`;

// ---------------------------------------------------------------------------
// Token storage.
//
// A bearer token in localStorage is readable by any script running on this
// origin, so an XSS anywhere in the app can lift it. The durable fix is an
// httpOnly, Secure, SameSite cookie issued by the backend — that is a backend
// change, not something this file can do alone. Until then: keep the panel's
// CSP tight and treat any third-party script added to this origin as having
// full access to the session.
// ---------------------------------------------------------------------------

export function getApiPanelToken() {
  try {
    return localStorage.getItem(API_PANEL_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setApiPanelToken(token) {
  try {
    localStorage.setItem(API_PANEL_TOKEN_KEY, token);
  } catch {
    /* storage unavailable (private mode, etc.) — ignore */
  }
}

export function clearApiPanelToken() {
  try {
    localStorage.removeItem(API_PANEL_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
